/* eslint-disable id-length */
import { List } from 'immutable';

import { NAMESPACE } from './constants';
import { records as testFrameworkRecords } from './../batch-framework-commands';

export const COMMAND_SELECT = `${NAMESPACE}/COMMAND_SELECT`;
export const COMMAND_ICON_SELECT = `${NAMESPACE}/COMMAND_ICON_SELECT`;
export const UPDATE_ACTIVE_COMMAND = `${NAMESPACE}/UPDATE_ACTIVE_COMMAND`;
export const CLEANUP = `${NAMESPACE}/CLEANUP`;
export const ASSETS_EXPIRED = `${NAMESPACE}/ASSETS_EXPIRED`;
export const NO_TEST_CASES_AVAILABLE = `${NAMESPACE}/NO_TEST_CASES_AVAILABLE`;
export const NO_COMMANDS_AVAILABLE = `${NAMESPACE}/NO_COMMANDS_AVAILABLE`;

export const PARSE_CYPRESS_JOB = `${NAMESPACE}/PARSE_CYPRESS_JOB`;
export const PARSE_PLAYWRIGHT_JOB = `${NAMESPACE}/PARSE_PLAYWRIGHT_JOB`;
export const PARSE_TESTCAFE_JOB = `${NAMESPACE}/PARSE_TESTCAFE_JOB`;
export const PARSE_PUPPETER_JOB = `${NAMESPACE}/PARSE_PUPPETER_JOB`;
export const PARSE_CUCUMBER_JOB = `${NAMESPACE}/PARSE_CUCUMBER_JOB`;

export const PARSE_TESTFAIRY_LOGS = `${NAMESPACE}/PARSE_TESTFAIRY_LOGS`;

export const SHOW_TEST_REPORT = `${NAMESPACE}/SHOW_TEST_REPORT`;

export const PARSE_CRASH_LOGS = `${NAMESPACE}/PARSE_CRASH_LOGS`;

export const UPDATE_JOB_DETAILS = `${NAMESPACE}/UPDATE_JOB_DETAILS`;

export const SELECT_SCREENSHOT = `${NAMESPACE}/SELECT_SCREENSHOT`;

/**
 * Asset represents the possible asset values.
 * Most cases, it will be a string which is simply the name of the asset.
 * Screenshots can be an array whose type depends on the source. For
 * virtual webdriver based jobs, it will simply be a string[]. For real
 * device jobs, it will be { id: string, url: string }[].
 */
export type Asset = string | string[] | { id: string, url: string }[];

/**
 * AssetMap is a mapping of keys to Assets in the store.
 */
export type AssetMap = Record<string, Asset>;

export function isStrings(array: unknown): array is string[] {
  return (Array.isArray(array) && array.every((item) => typeof item === 'string'));
}

export interface Screenshot {
  path: string;
  id: number;
  index: number;
  testCaseId?: number;
}

export interface ScreenshotList {
  size: number;

  all: () => Screenshot[];
  get: (index: number, testCaseId?: number) => Screenshot | undefined;
  next: (index: number, testCaseId?: number) => Screenshot | undefined;
  previous: (index: number, testCaseId?: number) => Screenshot | undefined;
}

export class TestCaseScreenshotList implements ScreenshotList {
  private screenshots: Screenshot[] = [];

  constructor(testCases: testFrameworkRecords.TestCase[]) {
    testCases.forEach((testcase) => {
      if (!testcase) {
        return;
      }

      const screenshots = (testcase.attachments as List<testFrameworkRecords.Attachment>)
        .filter((a) => !!a && a.contentType.startsWith('image'))
        .map((a: testFrameworkRecords.Attachment, index: number) => (
          {
            path: a.path,
            // Provide a stable id for use in both lookups and key-based rendering
            id: testcase.test_number + index,
            // Since this is a one-to-many relationship between a test case and its
            // screenshots, need a way to identify the screenshot relative to the test case
            index,
            // The parent test case for use in lookups
            testCaseId: testcase.test_number,
          }
        ))
        .toArray();
      this.screenshots.push(...screenshots);
    });
  }

  get size() {
    return this.screenshots.length;
  }

  all() {
    return this.screenshots;
  }

  get(index: number, testCaseId?: number) {
    return this.screenshots.find(
      (screenshot) => screenshot.index === index && screenshot.testCaseId === testCaseId,
    );
  }

  next(index: number, testCaseId?: number) {
    const globalIndex = this.screenshots.findIndex(
      (screenshot) => screenshot.index === index && screenshot.testCaseId === testCaseId,
    );
    if (globalIndex === -1) {
      return undefined;
    }

    return this.screenshots[(globalIndex + 1) % this.size];
  }

  previous(index: number, testCaseId?: number) {
    const globalIndex = this.screenshots.findIndex(
      (screenshot) => screenshot.index === index && screenshot.testCaseId === testCaseId,
    );
    if (globalIndex === -1) {
      return undefined;
    }

    const prevIndex = globalIndex === 0 ? this.size - 1 : globalIndex - 1;
    return this.screenshots[prevIndex];
  }
}

export class AssetScreenshotList implements ScreenshotList {
  private screenshots: Screenshot[];

  constructor(assets: string[]) {
    this.screenshots = [];
    assets.forEach((asset, index) => {
      this.screenshots.push({
        path: asset,
        id: index,
        index,
      });
    });
  }

  get size() {
    return this.screenshots.length;
  }

  all() {
    return this.screenshots;
  }

  get(index: number) {
    return this.screenshots[index];
  }

  next(index: number) {
    return this.screenshots[(index + 1) % this.size];
  }

  previous(index: number) {
    const prevIndex = index === 0 ? this.size - 1 : index - 1;
    return this.screenshots[prevIndex];
  }
}
